<template>
  <div class="card card-custom gutter-b mt-4">
    <div class="card-header border-0 pt-6">
      <div class="card-title d-flex flex-column">
        <div class="d-flex align-items-center mb-1">
          <h3 class="card-label font-weight-bolder text-dark mb-0 mr-3">
            การเปิด-ปิด ระดับสมาชิก
          </h3>
          <span
            class="label label-inline font-weight-bold"
            :class="
              savedEnabled ? 'label-light-success' : 'label-light'
            "
          >
            {{ savedEnabled ? "เปิดใช้งานอยู่" : "ปิดใช้งานอยู่" }}
          </span>
        </div>

        <span class="text-muted font-size-sm">
          ตั้งค่าระดับสมาชิกของทุกแคมเปญในบริษัทนี้
        </span>
      </div>

      <div class="card-toolbar d-flex flex-column align-items-end">
        <div class="d-inline-flex align-items-center mb-1">
          <span class="switch switch-sm switch-primary">
            <label class="mb-0">
              <input
                type="checkbox"
                v-model="isTierEnabled"
                :disabled="isLoading || isSaving || !isLoaded"
              />
              <span></span>
            </label>
          </span>
          <span
            class="ml-3 font-weight-bolder font-size-sm"
            :class="isTierEnabled ? 'text-primary' : 'text-muted'"
            style="width: 35px; text-align: left"
          >
            {{ isTierEnabled ? "เปิด" : "ปิด" }}
          </span>
        </div>
        <span class="text-muted font-size-xs text-right">
          {{
            isTierEnabled
              ? "สมาชิกจะได้รับระดับตามแต้มสะสมรวม"
              : "ปิดอยู่ — ลูกค้าจะไม่เห็นระดับสมาชิก"
          }}
        </span>
      </div>
    </div>

    <div class="card-body pb-6 pt-2">
      <div v-if="isLoading" class="text-center py-10">
        <div class="spinner spinner-primary spinner-lg mr-15"></div>
        <span class="text-muted font-weight-bold mt-2 d-block">
          กำลังโหลดข้อมูลการตั้งค่า...
        </span>
      </div>

      <div v-else>
        <div
          class="tier-preview-section mt-2"
          :style="{
            opacity: isTierEnabled ? 1 : 0.4,
            transition: 'opacity 0.2s ease-in-out'
          }"
        >
          <div class="d-flex align-items-center justify-content-between mb-4">
            <h6 class="font-weight-bolder text-dark font-size-base mb-0">
              ระดับที่ตั้งไว้
            </h6>
            <b-button
              variant="outline-primary"
              size="sm"
              class="font-weight-bold"
              :disabled="!isTierEnabled"
              @click="goToManageTier()"
            >
              จัดการระดับสมาชิก
            </b-button>
          </div>

          <div
            v-if="tierList.length === 0"
            class="text-muted font-weight-bold font-size-sm py-4 text-center"
          >
            ยังไม่มีระดับสมาชิกที่ตั้งไว้
          </div>

          <div v-else class="list-group list-group-flush">
            <div
              v-for="item in tierList"
              :key="item.id"
              class="d-flex align-items-center justify-content-between py-3 border-bottom border-light"
            >
              <div class="d-flex align-items-center">
                <span
                  class="badge-circle mr-3"
                  :style="{ backgroundColor: item.color || '#3699FF' }"
                ></span>
                <span class="font-weight-bolder text-dark font-size-lg">
                  {{ item.name }}
                </span>
              </div>

              <div class="font-weight-bold text-muted font-size-sm">
                {{ formatPointRange(item) }}
              </div>
            </div>
          </div>
        </div>

        <div
          class="d-flex justify-content-end align-items-center mt-6 pt-4 border-top"
        >
          <b-button
            v-if="isDirty"
            variant="light"
            class="font-weight-bold mr-2"
            :disabled="isSaving"
            @click="cancelChanges()"
          >
            ยกเลิก
          </b-button>

          <b-button
            variant="primary"
            :disabled="isLoading || isSaving || !isDirty || !isLoaded"
            @click="saveSettings()"
          >
            <span
              v-if="isSaving"
              class="spinner-border spinner-border-sm mr-1"
              role="status"
            ></span>
            บันทึกการตั้งค่า
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";
import {
  GET_COMPANY_MANAGEMENT_DATA,
  PUT_COMPANY_MEMBER_TIER
} from "@/core/services/store/company_management.module";

export default {
  name: "CompanyManagementMemberTier",
  props: {
    companyId: {
      type: [Number, String],
      default: 0
    }
  },
  data() {
    return {
      isLoading: false,
      isLoaded: false,
      isSaving: false,
      isTierEnabled: false,
      savedEnabled: false,
      tierList: []
    };
  },
  computed: {
    isDirty() {
      return this.isTierEnabled !== this.savedEnabled;
    }
  },
  watch: {
    companyId(newVal) {
      if (newVal > 0) {
        this.loadSettings();
      }
    }
  },
  created() {
    if (this.companyId > 0) {
      this.loadSettings();
    }
  },
  methods: {
    formatPointRange(item) {
      const minVal = item.min_point !== undefined ? item.min_point : item.min_points;
      const maxVal = item.max_point !== undefined ? item.max_point : item.max_points;
      const min = Number(minVal || 0).toLocaleString();

      if (maxVal !== null && maxVal !== undefined && maxVal !== "") {
        return `${min} – ${Number(maxVal).toLocaleString()} แต้ม`;
      }
      return `${min} แต้มขึ้นไป`;
    },

    cancelChanges() {
      this.isTierEnabled = this.savedEnabled;
    },

    goToManageTier() {
      if (!this.isTierEnabled || !this.companyId) return;
      this.$router.push(`/company-management/${this.companyId}/member-tier`);
    },

    async loadSettings() {
      if (!this.companyId || Number(this.companyId) <= 0) return;
      this.isLoading = true;
      this.isLoaded = false;

      try {
        const { data } = await this.$store.dispatch(
          GET_COMPANY_MANAGEMENT_DATA,
          { id: this.companyId }
        );
        if (data && data.status === "success" && data.data) {
          const enabled = Number(data.data.is_member_tier_enabled) === 1;
          this.savedEnabled = enabled;
          this.isTierEnabled = enabled;
          this.isLoaded = true;
        }

        try {
          ApiService.setHeader();
          const res = await ApiService.getParams("member_tier", {
            params: { company_id: this.companyId }
          });
          const items =
            res && res.data && res.data.data ? res.data.data.items : null;
          this.tierList = Array.isArray(items) ? items : [];
        } catch (error) {
          this.tierList = [];
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to load member tiers:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async saveSettings() {
      if (this.isSaving || !this.isDirty || this.isLoading || !this.isLoaded) return;
      if (!this.companyId || Number(this.companyId) <= 0) return;

      this.isSaving = true;

      try {
        const res = await this.$store.dispatch(PUT_COMPANY_MEMBER_TIER, {
          id: this.companyId,
          data: { is_member_tier_enabled: this.isTierEnabled ? 1 : 0 }
        });

        const resData = res && res.data ? res.data : res;
        if (resData && (resData.status === "success" || resData.data)) {
          this.savedEnabled = this.isTierEnabled;

          Swal.fire({
            icon: "success",
            title: "บันทึกการตั้งค่าสำเร็จ!",
            text: "การตั้งค่าระดับสมาชิกถูกบันทึกเรียบร้อยแล้ว",
            confirmButtonText: "ตกลง",
            customClass: {
              confirmButton: "btn btn-primary"
            }
          });
        } else {
          this.isTierEnabled = this.savedEnabled;

          Swal.fire({
            icon: "error",
            title: "เกิดข้อผิดพลาด",
            text:
              (resData && resData.error && (this.$te("RESPONSE_MESSAGE." + resData.error) ? this.$t("RESPONSE_MESSAGE." + resData.error) : null)) ||
              "ไม่สามารถบันทึกการตั้งค่าได้ กรุณาลองใหม่อีกครั้ง",
            confirmButtonText: "ตกลง",
            customClass: {
              confirmButton: "btn btn-danger"
            }
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to save member tier setting:", error);

        this.isTierEnabled = this.savedEnabled;

        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: "ไม่สามารถบันทึกการตั้งค่าได้ กรุณาลองใหม่อีกครั้ง",
          confirmButtonText: "ตกลง",
          customClass: {
            confirmButton: "btn btn-danger"
          }
        });
      } finally {
        this.isSaving = false;
      }
    }
  }
};
</script>

<style scoped>
.badge-circle {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.switch input:not(:checked) ~ span:before {
  background-color: #e4e6ef !important;
}

.switch input:not(:checked) ~ span:after {
  background-color: #ffffff !important;
  opacity: 1 !important;
}
</style>
